'use client';

import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';

import VideoPlayerModal from '@/components/video/VideoPlayerModal';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

type VideoLinkProps = PropsWithChildren<{
    url: string;
    id?: string;
    className?: string;
    trackingEvent?: string;
    onModalOpenChange?: (isOpen: boolean) => void;
}>;

/**
 * Anchor that opens the video player modal instead of navigating.
 * The href is kept on the anchor for accessibility; the click is intercepted.
 */
const VideoLink: React.FC<VideoLinkProps> = ({ url, id, className, trackingEvent, onModalOpenChange, children }) => {
    const { track } = useAnalytics();
    const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);

    useEffect(() => {
        onModalOpenChange?.(isPlayerModalOpen);
        return () => {
            // Reset the parent's state if the link unmounts while the modal is open
            if (isPlayerModalOpen) {
                onModalOpenChange?.(false);
            }
        };
    }, [isPlayerModalOpen, onModalOpenChange]);

    const openPlayerModal = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
            setIsPlayerModalOpen(true);
            track(trackingEvent ?? 'Video Link Played', {
                videoUrl: url
            });
        },
        [url, trackingEvent, track]
    );

    return (
        <>
            <VideoPlayerModal
                url={url}
                isOpen={isPlayerModalOpen}
                setIsOpen={setIsPlayerModalOpen}
            />
            <a
                id={id}
                href={url}
                className={className}
                rel="noreferrer"
                onClick={openPlayerModal}>
                {children}
            </a>
        </>
    );
};

export default VideoLink;
